/**
 * CityAutosuggest Component
 * Provides intelligent city name suggestions for North American cities
 */

import React, { useState, useRef, useEffect } from 'react';
import { 
  CityResult, 
  CityAutosuggestProps, 
  CityAutosuggestState,
  CityAutosuggestError,
  CachedCityResult,
  DEFAULT_CITY_AUTOSUGGEST_CONFIG 
} from '../types/cityAutosuggest.types';
import { useDebounce } from '../hooks/useDebounce';
import { CitySearchService } from '../services/citySearchService';
import styles from './CityAutosuggest.module.css';

const CityAutosuggest: React.FC<CityAutosuggestProps> = ({
  value,
  onChange,
  onSelect,
  placeholder = "Enter city name",
  disabled = false,
  className = '',
  enableAutosuggest = true,
  apiEndpoint,
  debounceMs = DEFAULT_CITY_AUTOSUGGEST_CONFIG.debounceMs,
  minCharacters = DEFAULT_CITY_AUTOSUGGEST_CONFIG.minCharacters,
  maxSuggestions = DEFAULT_CITY_AUTOSUGGEST_CONFIG.maxSuggestions,
  error,
  'data-testid': testId = 'city-autosuggest'
}) => {
  // Component state
  const [state, setState] = useState<CityAutosuggestState>({
    inputValue: value,
    suggestions: [],
    isLoading: false,
    isOpen: false,
    selectedIndex: -1,
    error: null,
    cache: new Map()
  });

  // Cache statistics for tracking
  const [, setCacheStats] = useState({
    hits: 0,
    misses: 0,
    totalRequests: 0
  });

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLUListElement>(null);
  const suggestionRefs = useRef<(HTMLLIElement | null)[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const liveRegionRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | null>(null);
  const requestIdRef = useRef<number>(0);
  const isUserInputRef = useRef<boolean>(false);
  const isInitialLoadRef = useRef<boolean>(true);

  // Debounced input value for API calls
  const debouncedInputValue = useDebounce(state.inputValue, debounceMs);

  // Create city search service instance with the provided API endpoint
  const citySearchService = React.useMemo(() => {
    return new CitySearchService(apiEndpoint || '/api');
  }, [apiEndpoint]);

  // Function to announce updates to screen readers
  const announceToScreenReader = (message: string) => {
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = message;
      // Clear the message after a short delay to allow for re-announcements
      setTimeout(() => {
        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = '';
        }
      }, 1000);
    }
  };

  // Cache management functions
  const getCacheKey = (query: string): string => {
    return query.toLowerCase().trim();
  };

  const getCachedResult = (query: string): CityResult[] | null => {
    const cacheKey = getCacheKey(query);
    const cached = state.cache.get(cacheKey);
    
    if (!cached) {
      return null;
    }

    // Check if cache entry has expired
    if (Date.now() > cached.expiresAt) {
      // Remove expired entry
      setState(prev => {
        const newCache = new Map(prev.cache);
        newCache.delete(cacheKey);
        return { ...prev, cache: newCache };
      });
      return null;
    }

    return cached.results;
  };

  const setCachedResult = (query: string, results: CityResult[]): void => {
    const cacheKey = getCacheKey(query);
    const now = Date.now();
    const cacheEntry: CachedCityResult = {
      query: cacheKey,
      results,
      timestamp: now,
      expiresAt: now + DEFAULT_CITY_AUTOSUGGEST_CONFIG.cacheExpiryMs
    };

    setState(prev => {
      const newCache = new Map(prev.cache);
      newCache.set(cacheKey, cacheEntry);
      return { ...prev, cache: newCache };
    });
  };

  const cleanupExpiredCache = (): void => {
    const now = Date.now();
    setState(prev => {
      const newCache = new Map(prev.cache);
      let removedCount = 0;

      for (const [key, entry] of newCache.entries()) {
        if (now > entry.expiresAt) {
          newCache.delete(key);
          removedCount++;
        }
      }

      if (removedCount > 0) {
        console.debug(`Cleaned up ${removedCount} expired cache entries`);
      }

      return { ...prev, cache: newCache };
    });
  };

  const updateCacheStats = (isHit: boolean): void => {
    setCacheStats(prev => ({
      hits: prev.hits + (isHit ? 1 : 0),
      misses: prev.misses + (isHit ? 0 : 1),
      totalRequests: prev.totalRequests + 1
    }));
  };

  // Cleanup function for requests and timeouts
  const cleanupRequest = () => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // Effect to handle API calls when debounced input changes
  useEffect(() => {
    // Skip API call on initial load or if input change was not user-initiated
    if (isInitialLoadRef.current || !isUserInputRef.current) {
      isInitialLoadRef.current = false;
      isUserInputRef.current = false;
      return;
    }

    if (!enableAutosuggest || debouncedInputValue.length < minCharacters) {
      // Clean up any pending requests
      cleanupRequest();
      
      setState(prev => ({
        ...prev,
        suggestions: [],
        isLoading: false,
        error: null,
        isOpen: false
      }));
      return;
    }

    // Clean up previous request
    cleanupRequest();

    // Create new abort controller and request ID for race condition prevention
    abortControllerRef.current = new AbortController();
    const currentRequestId = ++requestIdRef.current;

    const searchCities = async () => {
      // Check cache first
      const cachedResults = getCachedResult(debouncedInputValue);
      if (cachedResults !== null) {
        // Check if this is still the current request
        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        updateCacheStats(true); // Cache hit
        
        setState(prev => ({
          ...prev,
          suggestions: cachedResults,
          isLoading: false,
          error: null,
          isOpen: true
        }));

        // Announce the number of suggestions to screen readers
        const count = cachedResults.length;
        if (count === 0) {
          announceToScreenReader('No city suggestions found');
        } else if (count === 1) {
          announceToScreenReader('1 city suggestion available');
        } else {
          announceToScreenReader(`${count} city suggestions available`);
        }
        return;
      }

      // Cache miss - make API call
      updateCacheStats(false);

      // Check if this is still the current request before setting loading state
      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      setState(prev => ({
        ...prev,
        isLoading: true,
        error: null,
        isOpen: true
      }));

      // Set up timeout for the request (3 seconds)
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutRef.current = setTimeout(() => {
          const timeoutError = new Error('Request timeout');
          timeoutError.name = CityAutosuggestError.TIMEOUT;
          reject(timeoutError);
        }, 3000);
      });

      try {
        // Race between the API call and timeout
        const cities = await Promise.race([
          citySearchService.searchCities({
            query: debouncedInputValue,
            limit: maxSuggestions,
            signal: abortControllerRef.current?.signal
          }),
          timeoutPromise
        ]);

        // Clear the timeout since request completed
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        // Check if this is still the current request
        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        // Cache the results
        setCachedResult(debouncedInputValue, cities);

        setState(prev => ({
          ...prev,
          suggestions: cities,
          isLoading: false,
          error: null,
          isOpen: true
        }));

        // Announce the number of suggestions to screen readers
        const count = cities.length;
        if (count === 0) {
          announceToScreenReader('No city suggestions found');
        } else if (count === 1) {
          announceToScreenReader('1 city suggestion available');
        } else {
          announceToScreenReader(`${count} city suggestions available`);
        }
      } catch (error) {
        // Clear the timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        // Don't update state if request was aborted or if this is not the current request
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }

        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        let errorMessage = 'Unable to load suggestions';
        if (error instanceof Error) {
          switch (error.name) {
            case CityAutosuggestError.NETWORK_ERROR:
              errorMessage = 'Network error - please check your connection';
              break;
            case CityAutosuggestError.QUOTA_EXCEEDED:
              errorMessage = 'Service temporarily unavailable';
              break;
            case CityAutosuggestError.INVALID_QUERY:
              errorMessage = 'Invalid search query';
              break;
            case CityAutosuggestError.TIMEOUT:
              errorMessage = 'Request timed out - please try again';
              break;
            default:
              errorMessage = 'Unable to load suggestions';
          }
        }

        setState(prev => ({
          ...prev,
          suggestions: [],
          isLoading: false,
          error: errorMessage,
          isOpen: true
        }));
      }
    };

    searchCities();
  }, [debouncedInputValue, enableAutosuggest, minCharacters, maxSuggestions]);

  // Cleanup effect - runs on component unmount
  useEffect(() => {
    return () => {
      // Clean up all pending requests and timeouts
      cleanupRequest();
      
      // Clear cache cleanup interval if it exists
      // Note: The cache cleanup interval is handled by its own effect
    };
  }, []);

  // Cache cleanup effect - runs every 5 minutes
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      cleanupExpiredCache();
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      clearInterval(cleanupInterval);
    };
  }, []);

  // Update internal state when external value changes
  useEffect(() => {
    if (value !== state.inputValue) {
      // Mark this as programmatic change (not user input)
      isUserInputRef.current = false;
      
      setState(prev => ({
        ...prev,
        inputValue: value,
        isOpen: false,
        selectedIndex: -1
      }));
    }
  }, [value]);

  // Clean up suggestion refs when suggestions change
  useEffect(() => {
    suggestionRefs.current = suggestionRefs.current.slice(0, state.suggestions.length);
  }, [state.suggestions.length]);

  // Scroll selected suggestion into view and announce to screen readers
  useEffect(() => {
    if (state.selectedIndex >= 0 && suggestionRefs.current[state.selectedIndex]) {
      const selectedElement = suggestionRefs.current[state.selectedIndex];
      if (selectedElement && suggestionsRef.current) {
        const container = suggestionsRef.current;
        const elementTop = selectedElement.offsetTop;
        const elementBottom = elementTop + selectedElement.offsetHeight;
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;

        if (elementTop < containerTop) {
          // Scroll up to show the element
          container.scrollTop = elementTop;
        } else if (elementBottom > containerBottom) {
          // Scroll down to show the element
          container.scrollTop = elementBottom - container.clientHeight;
        }
      }

      // Announce the selected suggestion to screen readers
      if (state.suggestions[state.selectedIndex]) {
        const suggestion = state.suggestions[state.selectedIndex];
        announceToScreenReader(`${suggestion.name}, ${suggestion.state}, ${suggestion.country}`);
      }
    }
  }, [state.selectedIndex, state.suggestions]);

  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    
    // Mark this as user-initiated input change
    isUserInputRef.current = true;
    
    setState(prev => ({
      ...prev,
      inputValue: newValue,
      selectedIndex: -1,
      error: null
    }));

    // Call onChange immediately for controlled input
    onChange(newValue);

    // Show dropdown if we have suggestions or are loading
    if (newValue.length >= minCharacters || state.suggestions.length > 0) {
      setState(prev => ({ ...prev, isOpen: true }));
    } else {
      setState(prev => ({ ...prev, isOpen: false, suggestions: [] }));
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (state.suggestions.length > 0) {
      setState(prev => ({ ...prev, isOpen: true }));
    }
  };

  // Handle input blur
  const handleInputBlur = () => {
    // Delay closing to allow for suggestion clicks
    setTimeout(() => {
      setState(prev => ({ ...prev, isOpen: false, selectedIndex: -1 }));
    }, 150);
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: CityResult) => {
    const displayValue = suggestion.displayName;
    
    // Mark this as programmatic change (not user input)
    isUserInputRef.current = false;
    
    setState(prev => ({
      ...prev,
      inputValue: displayValue,
      isOpen: false,
      selectedIndex: -1,
      suggestions: []
    }));

    onChange(displayValue);
    
    if (onSelect) {
      onSelect(suggestion);
    }

    // Focus back to input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!state.isOpen && state.suggestions.length > 0) {
          // Open dropdown if closed but has suggestions
          setState(prev => ({ ...prev, isOpen: true, selectedIndex: 0 }));
        } else if (state.isOpen && state.suggestions.length > 0) {
          // Navigate down through suggestions
          setState(prev => ({
            ...prev,
            selectedIndex: prev.selectedIndex < prev.suggestions.length - 1 
              ? prev.selectedIndex + 1 
              : 0
          }));
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (state.isOpen && state.suggestions.length > 0) {
          // Navigate up through suggestions
          setState(prev => ({
            ...prev,
            selectedIndex: prev.selectedIndex > 0 
              ? prev.selectedIndex - 1 
              : prev.suggestions.length - 1
          }));
        }
        break;

      case 'Enter':
        if (state.isOpen && state.selectedIndex >= 0 && state.suggestions[state.selectedIndex]) {
          // Select the highlighted suggestion
          event.preventDefault();
          handleSuggestionClick(state.suggestions[state.selectedIndex]);
        }
        // If no suggestion is selected, allow default behavior (form submission)
        break;

      case 'Escape':
        event.preventDefault();
        if (state.isOpen) {
          // Close dropdown and reset selection
          setState(prev => ({
            ...prev,
            isOpen: false,
            selectedIndex: -1
          }));
          // Keep focus on input
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }
        break;

      case 'Tab':
        // Allow natural tab behavior but close dropdown
        if (state.isOpen) {
          setState(prev => ({
            ...prev,
            isOpen: false,
            selectedIndex: -1
          }));
        }
        break;

      case 'Home':
        if (state.isOpen && state.suggestions.length > 0) {
          event.preventDefault();
          setState(prev => ({ ...prev, selectedIndex: 0 }));
        }
        break;

      case 'End':
        if (state.isOpen && state.suggestions.length > 0) {
          event.preventDefault();
          setState(prev => ({ ...prev, selectedIndex: prev.suggestions.length - 1 }));
        }
        break;
    }
  };

  // Render loading indicator
  const renderLoadingIndicator = () => (
    <div className={styles.loadingIndicator} data-testid={`${testId}-loading`}>
      <span className={styles.spinner} />
      <span>Searching cities...</span>
    </div>
  );

  // Render empty state
  const renderEmptyState = () => (
    <div className={styles.emptyState} data-testid={`${testId}-empty`}>
      No cities found
    </div>
  );

  // Render error state
  const renderErrorState = () => (
    <div className={styles.errorState} data-testid={`${testId}-error`}>
      {state.error || 'Unable to load suggestions'}
    </div>
  );

  // Render suggestions list
  const renderSuggestions = () => {
    if (!state.isOpen) return null;

    return (
      <ul 
        ref={suggestionsRef}
        className={styles.suggestionsList}
        role="listbox"
        aria-label="City suggestions"
        id={`${testId}-suggestions`}
        data-testid={`${testId}-suggestions`}
      >
        {state.isLoading && (
          <li className={styles.suggestionItem}>
            {renderLoadingIndicator()}
          </li>
        )}
        
        {!state.isLoading && state.error && (
          <li className={styles.suggestionItem}>
            {renderErrorState()}
          </li>
        )}
        
        {!state.isLoading && !state.error && state.suggestions.length === 0 && state.inputValue.length >= minCharacters && (
          <li className={styles.suggestionItem}>
            {renderEmptyState()}
          </li>
        )}
        
        {!state.isLoading && !state.error && state.suggestions.map((suggestion, index) => (
          <li
            key={suggestion.placeId}
            ref={(el) => {
              suggestionRefs.current[index] = el;
            }}
            className={`${styles.suggestionItem} ${
              index === state.selectedIndex ? styles.suggestionItemSelected : ''
            }`}
            role="option"
            aria-selected={index === state.selectedIndex}
            onClick={() => handleSuggestionClick(suggestion)}
            onMouseEnter={() => {
              // Update selected index on mouse hover for better UX
              setState(prev => ({ ...prev, selectedIndex: index }));
            }}
            data-testid={`${testId}-suggestion-${index}`}
            id={`${testId}-option-${index}`}
          >
            <div className={styles.suggestionContent}>
              <span className={styles.cityName}>{suggestion.name}</span>
              <span className={styles.cityDetails}>
                {suggestion.state}, {suggestion.country}
              </span>
            </div>
          </li>
        ))}
      </ul>
    );
  };

  // If autosuggest is disabled, render simple input
  if (!enableAutosuggest) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={state.inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
        className={`${styles.input} ${error ? styles.inputError : ''} ${className}`}
        data-testid={testId}
        aria-describedby={error ? `${testId}-error` : undefined}
      />
    );
  }

  return (
    <div className={`${styles.container} ${className}`} data-testid={testId}>
      <input
        ref={inputRef}
        type="text"
        value={state.inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={`${styles.input} ${error ? styles.inputError : ''}`}
        role="combobox"
        aria-expanded={state.isOpen}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        aria-owns={state.isOpen ? `${testId}-suggestions` : undefined}
        aria-activedescendant={
          state.isOpen && state.selectedIndex >= 0 
            ? `${testId}-option-${state.selectedIndex}` 
            : undefined
        }
        aria-describedby={error ? `${testId}-error` : undefined}
        data-testid={`${testId}-input`}
      />
      
      {renderSuggestions()}
      
      {/* Live region for screen reader announcements */}
      <div
        ref={liveRegionRef}
        aria-live="polite"
        aria-atomic="true"
        className={styles.srOnly}
        data-testid={`${testId}-live-region`}
      />
      
      {error && (
        <span 
          id={`${testId}-error`} 
          className={styles.errorText} 
          role="alert"
          data-testid={`${testId}-error-message`}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default CityAutosuggest;